import React, { useEffect, useMemo, useState, useCallback } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import Wrapper from '../../components/Wrapper'
import { telecomunication } from '../../translate'
import '../../assets/style/style.css'
import Paper from '@mui/material/Paper'
import { Box, Typography, IconButton, FormControlLabel, Switch } from '@mui/material'
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew'
import DeleteIcon from '@mui/icons-material/Delete'
import Api from '../../Api'
import TelegramConnectModal from '../../components/new_component/TelegramConnectModal'
import { unreadChannelsSet } from '../../store/actions/users'

export default function Connects() {
  const { type } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const dispatch = useDispatch()
  const whatsappEnabled = useSelector((state) => !!state?.users?.unreadWhatsappEnabled)

  const langIndex = +localStorage.getItem('atsLang') || 1

  const [telegramData, setTelegramData] = useState(null)
  const [telegramModalOpen, setTelegramModalOpen] = useState(false)
  const [keepOnLeftMenu, setKeepOnLeftMenu] = useState(() => {
    return localStorage.getItem('telegram_keep_on_left_menu') === 'true'
  })

  // WhatsApp UI state
  const [waConnecting, setWaConnecting] = useState(false)
  const [waError, setWaError] = useState('')
  const [waResult, setWaResult] = useState(null)
  const [whatsappKeepOnLeftMenu, setWhatsappKeepOnLeftMenu] = useState(() => {
    return localStorage.getItem('whatsapp_keep_on_left_menu') === 'true'
  })

  const query = useMemo(() => new URLSearchParams(location.search), [location.search])

  useEffect(() => {
    if (type === 'telegram') {
      Api.telegramGet()
        .then((res) => setTelegramData(res.data.data?.[0] || null))
        .catch((err) => console.error('Failed to get telegram data:', err))
    }
  }, [type])

  // Read WhatsApp result from callback redirect query
  useEffect(() => {
    if (type !== 'whatsapp') return

    const ok = query.get('ok')
    const err = query.get('error')
    const error_description = query.get('error_description')
    const reqId = query.get('reqId')

    if (err) {
      setWaError(error_description ? `${err}: ${error_description}` : err)
      setWaResult(null)
      setWaConnecting(false)
      return
    }

    if (ok === '1') {
      const business_id = query.get('business_id')
      const waba_id = query.get('waba_id')
      const phone_number_id = query.get('phone_number_id')

      setWaError('')
      setWaConnecting(false)
      setWaResult({
        reqId,
        business_id,
        waba_id,
        phone_number_id,
      })

      // optional: clean url (remove query after showing)
      // navigate(location.pathname, { replace: true })
    }
  }, [type, query])

  // API-only OAuth start (no JS SDK)
  const startWhatsAppOauth = useCallback(() => {
    try {
      setWaError('')
      setWaResult(null)
      setWaConnecting(true)

      // IMPORTANT: This endpoint should be on the same domain (v-office.ats.am)
      // and should redirect to Meta OAuth dialog.
      window.location.href = '/rest/comunication/whatsapp/oauth/start'
    } catch (e) {
      setWaConnecting(false)
      setWaError(String(e?.message || e))
    }
  }, [])

  // =======================
  // UI components
  // =======================
  const Card = ({ title, desc, actionText, onAction, badge, status, onDelete, onClick }) => {
    const isClickable = status === 'ready' && onClick
    return (
      <div
        className="connect-card"
        onClick={isClickable ? onClick : undefined}
        style={isClickable ? { cursor: 'pointer' } : {}}
      >
        <div className="connect-card-header">
          <h4 className="connect-card-title">{title}</h4>
          {!!badge ? <div className="connect-card-badge">{badge}</div> : null}
        </div>

        <p className="connect-card-desc">{desc}</p>

        <div className="connect-card-action-wrapper">
          {status ? (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
              <Typography
                sx={{
                  fontSize: 13,
                  backgroundColor:
                    status === 'ready' ? '#7ebc0a' : status === 'error' ? '#ef4444' : '#6b7280',
                  color: '#fff',
                  fontWeight: 500,
                  padding: '4px 12px',
                  borderRadius: '12px',
                  display: 'inline-block',
                }}
              >
                {status === 'ready' ? telecomunication.ready[langIndex] : status === 'error' ? telecomunication.error[langIndex] : status}
              </Typography>
              {onDelete && (
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation()
                    onDelete()
                  }}
                  sx={{ color: '#ef4444', '&:hover': { color: '#dc2626' } }}
                  aria-label="Delete"
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              )}
            </Box>
          ) : (
            <button onClick={onAction} className="connect-card-action" disabled={!onAction}>
              {actionText}
            </button>
          )}
        </div>
      </div>
    )
  }

  const Section = ({ title, subtitle, children }) => (
    <div className="connect-section">
      <div className="connect-section-header">
        <h3 className="connect-section-title">{title}</h3>
        {subtitle ? <div className="connect-section-subtitle">{subtitle}</div> : null}
      </div>

      {waError && type !== 'telegram' ? (
        <div style={{ margin: '8px 0 12px', color: '#ef4444', fontSize: 13 }}>{waError}</div>
      ) : null}

      {waResult && type === 'whatsapp' ? (
        <div style={{ margin: '8px 0 12px', color: '#111827', fontSize: 13 }}>
          <div><b>Connected</b></div>
          {waResult.reqId ? <div>reqId: {waResult.reqId}</div> : null}
          <div>business_id: {waResult.business_id}</div>
          <div>waba_id: {waResult.waba_id}</div>
          <div>phone_number_id: {waResult.phone_number_id}</div>
        </div>
      ) : null}

      <div className="connect-section-grid">{children}</div>
    </div>
  )

  const handleDeleteTelegram = async () => {
    try {
      await Api.telegramDelete(telegramData.id)
      setTelegramData(null)
      dispatch(unreadChannelsSet({
        whatsappEnabled,
        telegramEnabled: false,
        telegramMainId: null,
      }))
    } catch (e) {
      console.error('Failed to delete telegram connection:', e)
    }
  }

  const telegramConnects = () => (
    <Section title={''} subtitle={''}>
      <Card
        title={telecomunication.telegram_handle_messaging[langIndex]}
        desc={telecomunication.telegram_handle_messaging_desc[langIndex]}
        actionText={telecomunication.action_open[langIndex]}
        onAction={() => navigate(`/connect/telegram-number`)}
        status={telegramData?.status || null}
        onClick={ () => navigate(`/connect/telegram-number`)}
      />

      {telegramModalOpen && !telegramData && (
        <TelegramConnectModal
          open={telegramModalOpen}
          onClose={() => setTelegramModalOpen(false)}
          onConnected={() => {
            setTelegramModalOpen(false)
            Api.telegramGet().then((res) => setTelegramData(res.data.data?.[0] || null))
          }}
        />
      )}

      <Card
        title={telecomunication.telegram_included_number[langIndex]}
        desc={telecomunication.telegram_included_number_desc[langIndex]}
        actionText={telecomunication.action_configure[langIndex]}
        onAction={() => {
          const basePath = location.pathname.includes('/connect/telegram')&& !location.pathname.includes('short_function/connect/telegram') ? '/connect/telegram' : '/short_function'
          navigate(`${basePath}/telegram_integr/included_number`)
        }}
      />

      <Card
        title={telecomunication.telegram_main_number[langIndex]}
        desc={telecomunication.telegram_main_number_desc[langIndex]}
        actionText={telecomunication.action_set_main[langIndex]}
        onAction={() => {
          const basePath = location.pathname.includes('/connect/telegram') && !location.pathname.includes('short_function/connect/telegram')? '/connect/telegram' : '/short_function'
          navigate(`${basePath}/telegram_integr/main_number`)
        }}
      />
    </Section>
  )

  const whatsappConnects = () => (
    <Section title={telecomunication.whatsapp_title[langIndex]} subtitle={telecomunication.whatsapp_subtitle[langIndex]}>
      <Card
        title={telecomunication.whatsapp_manage_numbers[langIndex]}
        desc={telecomunication.whatsapp_manage_numbers_desc[langIndex]}
        actionText={telecomunication.action_open[langIndex]}
        onAction={() => navigate(`/connect/whatsapp-number`)}
        onClick={() => navigate(`/connect/whatsapp-number`)}
      />
    </Section>
  )

  return (
    <Wrapper>
      <div className="tableParent">
        <Paper sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, borderBottom: '2px solid rgba(0, 0, 0, 0.08)', pb: 2.3, }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <IconButton
                size="small"
                sx={{ color: '#7ebc0a', fontSize: 18 }}
                onClick={() => navigate(-1)}
                aria-label="Back"
              >
                <ArrowBackIosNewIcon sx={{ fontSize: 20, fontWeight: 700 }} />
              </IconButton>
              <Typography variant="h5" sx={{ fontWeight: 700, m: 0 }}>
                {telecomunication.title[langIndex]}
              </Typography>
            </Box>
            {type === 'telegram' && (
              <FormControlLabel
                control={
                  <Switch
                    checked={keepOnLeftMenu}
                    onChange={(e) => {
                      const value = e.target.checked
                      setKeepOnLeftMenu(value)
                      localStorage.setItem('telegram_keep_on_left_menu', value ? 'true' : 'false')
                      if (value) {
                        localStorage.setItem('telegram', 'true')
                      } else {
                        localStorage.removeItem('telegram')
                      }
                    }}
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': {
                        color: '#7ebc0a',
                      },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                        backgroundColor: '#7ebc0a',
                      },
                    }}
                  />
                }
                label={langIndex === 1 ? 'Keep on the left menu' : langIndex === 2 ? 'Держать в левом меню' : 'Պահել ձախ մենյուում'}
              />
            )}
            {type === 'whatsapp' && (
              <FormControlLabel
                control={
                  <Switch
                    checked={whatsappKeepOnLeftMenu}
                    onChange={(e) => {
                      const value = e.target.checked
                      setWhatsappKeepOnLeftMenu(value)
                      localStorage.setItem('whatsapp_keep_on_left_menu', value ? 'true' : 'false')
                      if (value) {
                        localStorage.setItem('whatsapp', 'true')
                      } else {
                        localStorage.removeItem('whatsapp')
                      }
                    }}
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': {
                        color: '#7ebc0a',
                      },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                        backgroundColor: '#7ebc0a',
                      },
                    }}
                  />
                }
                label={langIndex === 1 ? 'Keep on the left menu' : langIndex === 2 ? 'Держать в левом меню' : 'Պահել ձախ մենյուում'}
              />
            )}
          </Box>

          {type === 'telegram' ? telegramConnects() : whatsappConnects()}
        </Paper>
      </div>
    </Wrapper>
  )
}
